import React, { useMemo, useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Col, ConfigProvider, Modal, Row, Space, Typography, Form } from "antd";
import {
  CheckCircleFilled,
  DownloadOutlined,
} from "@ant-design/icons";
import aictia from "../const/api";
import moment from "moment";
import html2canvas from "html2canvas/dist/html2canvas.min.js";
import jsPDF from "jspdf";

const { Paragraph, Text, Title } = Typography;

const LOGO_URL =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Aiktsa-logo.svg/1920px-Aiktsa-logo.svg.png";



const styles = {
  modalBody: {
    background: "#0c1524",
    border: "1px solid #1e293b",
    borderRadius: 16,
    boxShadow: "0 24px 70px rgba(0, 0, 0, 0.42)",
    padding: 24,
    position: "relative",
  },
  certificate: {
    position: "relative",
    overflow: "hidden",
    background: "#fffdfa",
    border: "4px double rgba(245, 158, 11, 0.72)",
    borderRadius: 14,
    boxShadow: "inset 0 2px 18px rgba(15, 23, 42, 0.08)",
    color: "#1e293b",
    padding: "32px 34px",
  },
  watermarkWrap: {
    alignItems: "center",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    opacity: 0.03,
    pointerEvents: "none",
    position: "absolute",
    right: 0,
    top: 0,
    userSelect: "none",
  },
  watermark: {
    height: 360,
    objectFit: "contain",
    width: 360,
  },
  content: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
  },
  logoWrap: {
    alignItems: "center",
    display: "flex",
    height: 64,
    justifyContent: "center",
    marginBottom: 16,
  },
  logo: {
    height: "100%",
    objectFit: "contain",
  },
  title: {
    color: "#0f172a",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: 0,
    lineHeight: 1.15,
    margin: "8px 0 0",
    textTransform: "none",
  },
  divider: {
    background: "#f59e0b",
    height: 2,
    margin: "14px auto",
    width: 96,
  },
  intro: {
    color: "#64748b",
    display: "block",
    fontSize: 12,
    fontStyle: "italic",
    lineHeight: 1.55,
    margin: "0 auto",
    maxWidth: 460,
  },
  detailsCard: {
    background: "#fcfbf7",
    border: "1px solid rgba(251, 191, 36, 0.42)",
    borderRadius: 12,
    boxShadow: "0 1px 8px rgba(15, 23, 42, 0.05)",
    display: "inline-block",
    margin: "22px auto 0",
    maxWidth: 560,
    padding: 18,
    textAlign: "center",
    width: "100%",
  },
  companyName: {
    color: "#0f172a",
    display: "block",
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: 0,
    lineHeight: 1.25,
    textTransform: "uppercase",
    wordBreak: "break-word",
  },
  rowsWrap: {
    borderTop: "1px solid rgba(203, 213, 225, 0.68)",
    marginTop: 16,
    paddingTop: 14,
    textAlign: "left",
  },
  label: {
    color: "#94a3b8",
    fontSize: 12,
    fontWeight: 700,
  },
  value: {
    color: "#1e293b",
    fontSize: 12,
    fontWeight: 700,
  },
  monoValue: {
    color: "#1e293b",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 12,
    fontWeight: 700,
  },
  footnote: {
    color: "#94a3b8",
    display: "block",
    fontSize: 10,
    fontStyle: "italic",
    lineHeight: 1.45,
    margin: "18px auto 0",
    maxWidth: 520,
  },
  association: {
    borderTop: "1px solid rgba(203, 213, 225, 0.68)",
    color: "#64748b",
    display: "block",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 1.4,
    lineHeight: 1.45,
    marginTop: 24,
    paddingTop: 14,
    textTransform: "uppercase",
  },
  footer: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "space-between",
    marginTop: 18,
  },
  qrBlock: {
    alignItems: "center",
    display: "flex",
    gap: 9,
    textAlign: "left",
  },
  qr: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    boxShadow: "0 1px 6px rgba(15, 23, 42, 0.06)",
    height: 58,
    padding: 4,
    width: 58,
  },
  qrTitle: {
    color: "#94a3b8",
    display: "block",
    fontSize: 8,
    fontWeight: 800,
    letterSpacing: 0.8,
    lineHeight: 1,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  qrText: {
    color: "#64748b",
    display: "block",
    fontSize: 9,
    fontWeight: 600,
    lineHeight: 1.22,
    maxWidth: 105,
  },
  validBadge: {
    alignItems: "center",
    background: "#ecfdf5",
    border: "1px solid #d1fae5",
    borderRadius: 8,
    color: "#047857",
    display: "inline-flex",
    flexDirection: "column",
    fontSize: 8,
    fontWeight: 900,
    gap: 2,
    letterSpacing: 0.7,
    padding: "7px 13px",
    textTransform: "uppercase",
  },
  signature: {
    textAlign: "right",
  },
  signatureTitle: {
    color: "#94a3b8",
    display: "block",
    fontSize: 9,
    fontWeight: 800,
    textTransform: "uppercase",
  },
  signatureName: {
    color: "#1e293b",
    display: "block",
    fontSize: 11,
    fontWeight: 900,
  },
  signatureLine: {
    background: "#cbd5e1",
    height: 2,
    marginLeft: "auto",
    marginTop: 5,
    width: 52,
  },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 20,
  },
};
const printStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;800&family=Inter:wght@400;600;800&display=swap');

  body {
    background-color: #faf9f6;
    font-family: 'Inter', sans-serif;
    margin: 0;
    padding: 20px;
    text-align: center;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .border-outer {
    border: 2px solid #b45309;
    margin: 0 auto;
    max-width: 800px;
    padding: 4px;
  }

  .border-box {
    background-color: #fffdfa;
    border: 6px double #b45309;
    overflow: hidden;
    padding: 40px;
    position: relative;
  }

  .watermark {
    height: 250px;
    left: 50%;
    object-fit: contain;
    opacity: 0.04;
    pointer-events: none;
    position: absolute;
    top: 55%;
    transform: translate(-50%, -50%);
    width: 350px;
    z-index: 1;
  }

  .content-container {
    position: relative;
    z-index: 10;
  }

  .logo {
    height: 80px;
    margin-bottom: 15px;
    object-fit: contain;
  }

  h1 {
    color: #0f172a;
    font-family: 'Playfair Display', serif;
    font-size: 28px;
    font-weight: 800;
    margin: 10px 0;
    text-transform: uppercase;
  }

  .divider {
    background-color: #d97706;
    height: 2px;
    margin: 15px auto;
    width: 120px;
  }

  .intro-text {
    color: #475569;
    font-size: 13px;
    font-style: italic;
    line-height: 1.5;
    margin: 0 auto 25px;
    max-width: 500px;
  }

  .company-name {
    color: #1e3a8a;
    font-family: 'Playfair Display', serif;
    font-size: 24px;
    font-weight: 800;
    letter-spacing: 1px;
    margin: 20px 0;
    text-transform: uppercase;
  }

  .details-table {
    border-collapse: collapse;
    font-size: 13px;
    margin: 0 auto 30px;
    width: 80%;
  }

  .details-table td {
    border-bottom: 1px solid #f1f5f9;
    padding: 8px 12px;
  }

  .details-table td.label {
    color: #64748b;
    font-weight: 600;
    text-align: left;
    width: 35%;
  }

  .details-table td.val {
    color: #0f172a;
    font-weight: 700;
    text-align: left;
  }

  .org-footer-title {
    border-top: 1px solid #e2e8f0;
    color: #475569;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 2px;
    margin-top: 35px;
    padding-top: 15px;
    text-align: center;
    text-transform: uppercase;
  }

  .footer-grid {
    align-items: center;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 15px;
    padding-top: 15px;
  }

  .footer-left {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    text-align: left;
  }

  .footer-center {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .footer-right {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    text-align: right;
  }

  .qr-img {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    height: 70px;
    padding: 3px;
    width: 70px;
  }

  .qr-label {
    color: #64748b;
    font-size: 9px;
    font-weight: 600;
    margin-top: 5px;
  }

  .badge-valid {
    align-items: center;
    background-color: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 20px;
    color: #047857;
    display: inline-flex;
    font-size: 10px;
    font-weight: 800;
    gap: 4px;
    letter-spacing: 1px;
    padding: 6px 14px;
  }

  .signature-title {
    color: #64748b;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  .signature-name {
    color: #0f172a;
    font-size: 13px;
    font-weight: 800;
  }

  .signature-line {
    background-color: #94a3b8;
    height: 1px;
    margin-top: 6px;
    width: 80px;
  }
`;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export default function RegistryCertificateModal({
  open,
  onClose,
  certificateId,
}) {
  const { id: routeCertificateId } = useParams();
  const history = useHistory();
  const effectiveCertificateId = certificateId || routeCertificateId;
  const isRoute = Boolean(routeCertificateId);
  const modalOpen = open || isRoute;
  const handleClose = onClose || (() => history.push("/"));

  const [form] = Form.useForm();
  const [certificate, setCertificate] = useState(null);
  const QR_URL =
    "https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=" +
    encodeURIComponent(`https://reyestr.aictia.org/#/verify/${effectiveCertificateId}`);

const formatRepresentative = (name) => {
  if (!name) return "-";

  const parts = name.trim().split(" ");

  const removeWords = ["OĞLU", "OGLU", "QIZI", "QIZ"];

  const filtered = parts.filter(
    (x) => !removeWords.includes(x.toUpperCase())
  );

  return filtered.slice(0, 2).join(" ");
};

  const certificateRef = useRef();

  useEffect(() => {
    if (effectiveCertificateId) {
      aictia
        .get(`ReyestrOfCompany/details/${effectiveCertificateId}`)
        .then((res) => {
          const data = res.data;

          const activityNames = data.typeOfActivityCompanyDto
            ?.map((item) => item.name)
            .join(", ");

          const categoryNames = data.activityCompanyDto
            ?.map((item) => item.name)
            .join(", ");

          setCertificate({
            title: "İKT Reyestr Şəhadətnaməsi",

            companyName: data.nameOfCompany || "-",

            voen: data.tin || "-",

            representative: formatRepresentative(data.officialCEOoftheCompany) || "-",

            documentNo: data.documentNo || "AICTIA-REY",

            registeredDate: data.dateOfIncorporationCompany
              ? moment(data.dateOfIncorporationCompany).format("DD.MM.YYYY")
              : "-",

            activityArea: activityNames || "-",

            category: categoryNames || "-",

            verifyUrl: `https://aictia.az/verify/${data.tin}`,

            chairman: "Elvin ABBASOV",
          });


          form.setFieldsValue({
            ...data,

            typeOfCompanyId: data.typeOfCompany?.name,

            packetOfAictiaId:
              data.packetOfAictiaCompanyDto?.map((l) => l.name),

            typeOfActivityId:
              data.typeOfActivityCompanyDto
                ?.filter((l) => l.isMain)
                .map((l) => l.name),

            typeOfActivitySubId:
              data.typeOfActivityCompanyDto
                ?.filter((l) => !l.isMain)
                .map((l) => l.name),

            activityId:
              data.activityCompanyDto
                ?.filter((l) => l.isMain)
                .map((l) => l.name),

            activitySubId:
              data.activityCompanyDto
                ?.filter((l) => !l.isMain)
                .map((l) => l.name),
          });

          form.setFieldsValue({
            type: data.phoneOfCompany,
          });
        })
        .catch(() => {
          console.error("Xəta baş verdi");
        });
    }
  }, [effectiveCertificateId]);

  const rows = useMemo(() => {
    return certificate
      ? [
        ["VÖEN:", certificate.voen],
        ["Rəsmi Təmsilçi:", certificate.representative],
        ["Reyestr Kateqoriyası:", certificate.category],
        ["Fəaliyyət Sahəsi:", certificate.activityArea],
        // ["Sənəd Qeydiyyat №:", certificate.documentNo],
        ["Nəşr Tarixi:", certificate.registeredDate],
      ]
      : [];
  }, [certificate]);

  // const handleDownloadPdf = async function () {
  //   if (!certificate || !certificateRef.current) return;

  //   try {
  //     const canvas = await html2canvas(certificateRef.current, {
  //       useCORS: true,
  //       allowTaint: true,
  //       scale: 2,
  //     });

  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  //     const pageWidth = pdf.internal.pageSize.getWidth();
  //     const pageHeight = pdf.internal.pageSize.getHeight();
  //     const imageProps = pdf.getImageProperties(imgData);
  //     const imgWidth = pageWidth;
  //     const imgHeight = (imageProps.height * pageWidth) / imageProps.width;
  //     let position = 0;

  //     pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);

  //     if (imgHeight > pageHeight) {
  //       let remainingHeight = imgHeight - pageHeight;
  //       while (remainingHeight > 0) {
  //         position = remainingHeight - imgHeight;
  //         pdf.addPage();
  //         pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  //         remainingHeight -= pageHeight;
  //       }
  //     }

  //     pdf.save(`${certificate.companyName || "certificate"}.pdf`);
  //   } catch (err) {
  //     console.error("PDF download failed", err);
  //   }
  // };

    function buildPrintHtml() {
    const tableRows = rows
      .map(function (row) {
        return (
          "<tr>" +
          '<td class="label">' +
          escapeHtml(row[0]) +
          "</td>" +
          '<td class="val">' +
          escapeHtml(row[1]) +
          "</td>" +
          "</tr>"
        );
      })
      .join("");

    return `
    <html>
      <head>
        <title>Şəhadətnamə - ${escapeHtml(certificate.companyName)}</title>
        <style>${printStyles}</style>
      </head>
      <body>
        <div class="border-outer">
          <div class="border-box">
            <img src="${LOGO_URL}" class="watermark" alt="" />
            <div class="content-container">
              <img src="${LOGO_URL}" class="logo" alt="AIKTSA Logo" />
              <h1>${escapeHtml(certificate.title)}</h1>
              <div class="divider"></div>
              <div class="intro-text">
                Bu şəhadətnamə rəsmi şəkildə təsdiq edir ki, aşağıda adı qeyd olunan subyekt AICTIA İKT Reyestr Portalı tərəfindən rəsmi qeydiyyata alınmışdır:
              </div>
              <div class="company-name">${escapeHtml(certificate.companyName)}</div>
              <table class="details-table">${tableRows}</table>
              <div class="org-footer-title">Azərbaycan İnformasiya və Kommunikasiya Texnologiyaları Sənayesi Assosiasiyası</div>
              <div class="footer-grid">
                <div class="footer-left">
                  <img src="${QR_URL}" class="qr-img" alt="Verification QR" />
                  <div class="qr-label">Həqiqiliyi yoxlamaq üçün oxudun</div>
                </div>
                <div class="footer-center">
                  <div class="badge-valid">✔ AICTIA VALID</div>
                </div>
                <div class="footer-right">
                  <div class="signature-title">Sədr:</div>
                  <div class="signature-name">${escapeHtml(certificate.chairman)}</div>
                  <div class="signature-line"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
    </html>
  `;
  }
  const handleDownloadPdf = async () => {
  if (!certificate) return;

  const iframe = document.createElement("iframe");

  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = "900px";
  iframe.style.height = "1200px";
  iframe.style.border = "0";

  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;

  doc.open();
  doc.write(buildPrintHtml());
  doc.close();

  await new Promise((resolve) => {
    iframe.onload = resolve;
  });

  // şəkillərin yüklənməsini gözlə
  await Promise.all(
    Array.from(doc.images).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) resolve();
          else {
            img.onload = resolve;
            img.onerror = resolve;
          }
        })
    )
  );

  const canvas = await html2canvas(doc.body, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
  });

  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF("p", "mm", "a4");

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

  pdf.save(`${certificate.companyName}.pdf`);

  document.body.removeChild(iframe);
};
  return (
    <ConfigProvider
      theme={{
        token: {
          borderRadius: 8,
          colorPrimary: "#0c82f4",
          fontFamily:
            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
      }}
    >
      <Modal
        centered
        closable={false}
        destroyOnClose
        footer={null}
        maskClosable
        open={modalOpen}
        onCancel={handleClose}
        visible={modalOpen}
        width={750}
        wrapClassName="certificate-modal"
        bodyStyle={{
          maxHeight: "90vh",
          overflowY: "auto",
          background: "transparent",
          boxShadow: "none",
          padding: 0,
        }}
      ><div style={styles.modalBody}>

          <div style={styles.certificate} ref={certificateRef}>
            <div style={styles.watermarkWrap}>
              <img
                alt=""
                referrerPolicy="no-referrer"
                src={LOGO_URL}
                style={styles.watermark}
              />
            </div>

            <div style={styles.content}>
              <div style={styles.logoWrap}>
                <img
                  alt="AIKTSA Logo"
                  referrerPolicy="no-referrer"
                  src={LOGO_URL}
                  style={styles.logo}
                />
              </div>

              <Title level={4} style={styles.title}>
                {certificate?.title}
              </Title>

              <div style={styles.divider} />

              <Paragraph style={styles.intro}>
                Bu şəhadətnamə rəsmi şəkildə təsdiq edir ki, aşağıda adı
                qeyd olunan təşkilat AICTIA İKT Reyestr Portalı tərəfindən
                qeydə alınmışdır:
              </Paragraph>

              <div style={styles.detailsCard}>
                <Text style={styles.companyName}>
                  {certificate?.companyName}
                </Text>

                <div style={styles.rowsWrap}>
                  <Row gutter={[12, 12]}>
                    {rows.map(function (row) {
                      const isMono =
                        row[0] === "VÖEN:" ||
                        row[0] === "Sənəd Qeydiyyat №:";

                      return (
                        <Col key={row[0]} xs={24} sm={12}>
                          <Text style={styles.label}>{row[0]}</Text>{" "}
                          <Text
                            style={isMono ? styles.monoValue : styles.value}
                          >
                            {row[1]}
                          </Text>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              </div>

              <Text style={styles.footnote}>
                * Bu reyestr Şəhadətnaməsi rəsmi partnyorluq statusunu
                təsdiq etmək, vergi güzəşt müraciirlərində iştirak etmək və
                tender layihələrində istifadə olunmaq üçün qanuni sənəddir.
              </Text>

              <Text style={styles.association}>
                Azərbaycan İnformasiya və Kommunikasiya Texnologiyaları
                Sənayesi Assosiasiyası
              </Text>

              <div style={styles.footer}>
                <div style={styles.qrBlock}>
                  <img
                    alt="Verification QR"
                    referrerPolicy="no-referrer"
                    src={QR_URL}
                    style={styles.qr}
                  />
                  <div>
                    <Text style={styles.qrTitle}>QR DOĞRULAMA</Text>
                    <Text style={styles.qrText}>
                      Həqiqiliyi yoxlamaq üçün oxudun
                    </Text>
                  </div>
                </div>

                <div style={styles.validBadge}>
                  <CheckCircleFilled style={{ color: "#059669" }} />
                  <span>AICTIA VALID</span>
                </div>

                <div style={styles.signature}>
                  <Text style={styles.signatureTitle}>Sədr:</Text>
                  <Text style={styles.signatureName}>
                    {certificate?.chairman}
                  </Text>
                  <div style={styles.signatureLine} />
                </div>
              </div>
            </div>
          </div>

          <div style={styles.actions}>
            <Space size={8}>
              <Button
                onClick={onClose}
                style={{
                  background: "#1e293b",
                  borderColor: "#1e293b",
                  color: "#cbd5e1",
                  fontWeight: 700,
                }}
              >
                Geri Qayıt
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadPdf}
                style={{ fontWeight: 700 }}
                type="primary"
              >
                PDF kimi yüklə
              </Button>
            </Space>
          </div>
        </div></Modal>
    </ConfigProvider>
  );
}
